<template>
  <article class="geogrid-wrapper">
    <InmapBusiness v-if="geogrid && features.inmapBusiness" :geogrid="geogrid" @selectCompetitor="selectCompetitor" />

    <div id="map" />

    <div v-if="features.searchZones && mapReady" class="toggle-cubes maplibregl-ctrl maplibregl-ctrl-group">
      <button type="button"
        :title="`${showSearchZones ? 'Hide' : 'Show'} search zones`"
        @click.prevent="toggleLayer('showSearchZones', 'search-zones-layer')">
        <i :class="['fa-solid fa-fw fa-grid', { 'text-muted': !showSearchZones }]" />
      </button>
    </div>

    <div v-if="features.zipZones && mapReady" class="toggle-zipcodes maplibregl-ctrl maplibregl-ctrl-group">
      <button type="button"
        :title="`${showZipZones ? 'Hide' : 'Show'} zip code zones`"
        @click.prevent="toggleLayer('showZipZones', ['zipcodes-polygons-layer', 'zipcodes-lines-layer'])">
        <i :class="['fa-solid fa-fw fa-envelope', { 'text-muted': !showZipZones }]" />
      </button>
    </div>

    <InmapStats v-if="geogrid && features.inmapStats" :geogrid="geogrid" />
  </article>
</template>

<script>
import * as maptilersdk from '@maptiler/sdk'

import calcGrid, { calcGridPointBoundaries } from 'src/vue_widgets/geogrids/map/geo_direct.js'
import { createFeatureCollection, createPoint, createPolygon } from 'src/geogrid_3d/geojson_helpers'

import InmapStats from './inmap_stats'
import InmapBusiness from './inmap_business'

const DEFAULT_FEATURES = {
  inmapStats: false,
  searchZones: false,
  zipZones: false,
  inmapBusiness: false
}

const COLORS_MAP = {
  1: '#01B52E',
  2: '#54D700',
  3: '#5BCF24',
  4: '#80D01E',
  5: '#9CD019',
  6: '#BBD014',
  7: '#D2CE0F',
  8: '#E0C50B',
  9: '#F9C403',
  10: '#F9B403',
  11: '#F39201',
  12: '#FC7902',
  13: '#FD5B01',
  14: '#F35205',
  15: '#E8490A',
  16: '#DE400E',
  17: '#D72810',
  18: '#CE1111',
  19: '#B50C0C',
  20: '#950606',
  X: '#6C0101',
  D: '#C0C0C0'
}

const POSTALS_FILTER = [
  'all',
  ['==', ['get', 'level'], 1],
  ['!', ['has', 'level_2']]
]

const preloadMapIcons = (map) => {
  const icons = ['X', '!', '1-medal-silver', '1-medal', '2-medal-silver', '2-medal', '3-medal-silver', '3-medal']
  for (let i = 0; i <= 20; i++) {
    icons.push(i)
  }

  icons.forEach((icon) => {
    map.loadImage(`/icons/${icon}.png`, (error, image) => {
      if (error) return
      if (!map.hasImage(`icon-${icon}`)) map.addImage(`icon-${icon}`, image)
    })
  })
}

const getMapPoints = (grid, geogridRanks) => {
  const points = []
  let index = 0

  geogridRanks.forEach((row) => {
    row.forEach((cell) => {
      const coords = grid[index].reverse()
      points.push(createPoint(coords, { icon: `icon-${cell}` }))
      index += 1
    })
  })

  return points
}

const getMapCubes = (grid, geogridRanks, gridSize) => calcGridPointBoundaries(grid).map((coordinates, i) => {
  const rankRow = Math.floor(i / gridSize)
  const rankCell = i % gridSize

  return createPolygon(
    [coordinates],
    {
      level: 1,
      height: gridSize * 10,
      baseHeight: 0,
      color: COLORS_MAP[geogridRanks[rankRow][rankCell]]
    },
    {}
  )
})

const randomRGBColor = () => {
  const random = () => Math.floor(Math.random() * 256)
  return `rgb(${random()}, ${random()}, ${random()})`
}

export default {
  components: { InmapBusiness, InmapStats },
  props: {
    geogrid: { type: Object },
    mapKey: { type: String, required: true },
    initialCenter: { type: Array, required: true },
    features: {
      type: Object,
      deafult: () => DEFAULT_FEATURES,
      validator(val) {
        if (typeof val !== 'object') return false

        const entries = Object.entries(val)
        for (let i = 0; i < entries.length; i++) {
          const [key, bool] = entries[i]
          if (!Object.prototype.hasOwnProperty.call(DEFAULT_FEATURES, key) || typeof bool !== 'boolean') return false
        }

        return true
      }
    }
  },
  data() {
    return {
      mapReady: false,
      showSearchZones: false,
      showZipZones: false
    }
  },
  mounted() {
    maptilersdk.config.apiKey = this.mapKey
    const map = new maptilersdk.Map({
      container: 'map',
      style: maptilersdk.MapStyle.STREETS,
      center: this.initialCenter,
      zoom: 14,
      maxZoom: 17,
      minZoom: 5,
      navigationControl: true,
      terrainControl: true,
      pitch: 35,
      bearing: 90,
      attributionControl: false,
      maptilerLogo: false
    })

    preloadMapIcons(map)

    map.on('load', () => {
      map.addSource('postalSource', {
        type: 'vector',
        url: `https://api.maptiler.com/tiles/countries/tiles.json?key=${this.mapKey}`
      })

      map.addLayer({
        id: 'zipcodes-polygons-layer',
        type: 'fill',
        source: 'postalSource',
        'source-layer': 'postal',
        filter: POSTALS_FILTER,
        paint: {
          'fill-color': [
            'case',
            ['==', ['typeof', ['feature-state', 'color']], 'string'],
            ['feature-state', 'color'],
            'rgba(0, 0, 0, 0)'
          ],
          'fill-opacity': 0.25
        },
        layout: {
          visibility: 'visible'
        }
      })

      map.addLayer({
        id: 'zipcodes-lines-layer',
        type: 'line',
        source: 'postalSource',
        'source-layer': 'postal',
        filter: ['==', 'level', 1],
        paint: {
          'line-color': [
            'case',
            ['==', ['typeof', ['feature-state', 'color']], 'string'],
            ['feature-state', 'color'],
            'rgba(0, 0, 0, 0)'
          ],
          'line-width': 3,
          'line-opacity': 0.5
        },
        layout: {
          visibility: 'visible'
        }
      })

      map.addSource('ranks-source', {
        type: 'geojson',
        data: createFeatureCollection([])
      })

      map.addLayer({
        id: 'ranks-layer',
        source: 'ranks-source',
        type: 'symbol',
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-overlap': 'always',
          'text-font': [
            'Open Sans Bold',
            'Arial Unicode MS Bold'
          ],
          'text-size': 11,
          'text-transform': 'uppercase',
          'text-letter-spacing': 0.05,
          'text-offset': [0, 1.5]
        }
      })

      map.addSource('search-zones-source', {
        type: 'geojson',
        data: createFeatureCollection([])
      })

      map.addLayer({
        id: 'search-zones-layer',
        source: 'search-zones-source',
        type: 'fill-extrusion',
        paint: {
          'fill-extrusion-color': ['get', 'color'],
          'fill-extrusion-height': ['get', 'height'],
          'fill-extrusion-base': ['get', 'baseHeight'],
          'fill-extrusion-opacity': 0.5
        },
        layout: {
          visibility: 'none'
        }
      })

      const setStates = () => {
        map.setLayoutProperty('zipcodes-polygons-layer', 'visibility', 'none')
        map.setLayoutProperty('zipcodes-lines-layer', 'visibility', 'none')

        const postals = map.querySourceFeatures('postalSource', {
          sourceLayer: 'postal',
          filter: POSTALS_FILTER
        })

        postals.forEach((row) => {
          map.setFeatureState({
            source: 'postalSource',
            sourceLayer: 'postal',
            id: row.id
          }, { color: randomRGBColor() })
        })
      }

      const afterLoad = (e) => {
        if (e.sourceId === 'postalSource' && e.isSourceLoaded) {
          setTimeout(setStates, 1500)
          map.off('sourcedata', afterLoad)
        }
      }

      if (map.isSourceLoaded('postalSource')) {
        setStates()
      } else {
        map.on('sourcedata', afterLoad)
      }

      map.on('click', 'zipcodes-polygons-layer', (e) => {
        new maptilersdk.Popup()
          .setLngLat(e.lngLat)
          .setHTML(`<b>${e.features[0].properties.code}</b>`)
          .addTo(map)
      })

      this.mapReady = true
      this.renderGeogrid()
    })

    this.map = map
  },
  methods: {
    renderGeogrid() {
      if (this.geogrid) {
        const { ranks, attrs } = this.geogrid
        const {
          gridCenterLat, gridCenterLng, gridSize, distanceInMeters
        } = attrs

        const grid = calcGrid(gridCenterLat, gridCenterLng, gridSize, distanceInMeters)
        const pointFeatures = getMapPoints(grid, ranks)

        const features = createFeatureCollection(pointFeatures)
        this.map.getSource('ranks-source').setData(features)

        const cubeFeatures = getMapCubes(grid, ranks, gridSize)
        this.map.getSource('search-zones-source').setData(createFeatureCollection(cubeFeatures))

        this.map.fitBounds(
          [
            pointFeatures[0].geometry.coordinates,
            pointFeatures.at(-1).geometry.coordinates
          ],
          {
            padding: {
              top: 20,
              bottom: 75,
              left: 100,
              right: 100
            }
          }
        )
      } else {
        this.map.getSource('ranks-source').setData(createFeatureCollection([]))
        this.map.getSource('search-zones-source').setData(createFeatureCollection([]))
      }
    },
    toggleLayer(key, layer) {
      this[key] = !this[key]
      const visibility = this[key] ? 'visible' : 'none'

      if (Array.isArray(layer)) {
        layer.forEach((l) => this.map.setLayoutProperty(l, 'visibility', visibility))
      } else {
        this.map.setLayoutProperty(layer, 'visibility', visibility)
      }
    },
    selectCompetitor(competitorIndex) {
      this.$emit('selectCompetitor', competitorIndex)
    }
  },
  watch: {
    geogrid() {
      if (this.mapReady) {
        this.renderGeogrid()
      }
    }
  }
}
</script>
